import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import posed, { PoseGroup } from 'react-pose';

import { formatCurrency } from 'utils';
import Icon from 'common/icon';
import Collapsible from 'common/collapsible';

const DropdownPose = posed.div({
    enter: { opacity: 1, translateY: 0 },
    exit: { opacity: 0, translateY: -10 },
});

const TipPresetSelect = ({ options, selected, onSelect, onCustom }) => (
    <Collapsible>
        {({ isExpanded, toggle, expand, collapse }) => (
            <div className="relative">
                <button
                    type="button"
                    className="absolute pin-t pin-r z-20 py-6 px-4 mr-2"
                    onClick={toggle}
                    data-testid="tip-preset-dropdown"
                >
                    <Icon icon="caret-down" type="solid" className="text-18 text-gray-30" />
                </button>

                <div
                    className={cx('border border-gray-20 rounded', {
                        'opacity-0': isExpanded,
                    })}
                >
                    <button type="button" className="text-left p-6 pr-14" onClick={expand}>
                        <p className="text-gray-70 font-medium">
                            <span className="inline-block text-right w-14">{selected.label}</span>
                            <span className="inline-block w-32 pl-4">
                                {formatCurrency(selected.value, { cents: 'always' })}
                            </span>
                        </p>
                    </button>
                </div>

                <PoseGroup withParent={false}>
                    {isExpanded && (
                        <DropdownPose
                            key="dropdown"
                            className="border border-gray-20 rounded absolute pin-t pin-r bg-white shadow-md z-10"
                        >
                            {options.map(option => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className="text-left p-6 pr-14 hover:bg-blue-10"
                                    data-testid="tip-amount-preset"
                                    onClick={() => {
                                        onSelect(option);
                                        collapse();
                                    }}
                                >
                                    <p
                                        className={cx('font-medium', {
                                            'text-gray-70': option.value !== selected.value,
                                            'text-blue-50': option.value === selected.value,
                                        })}
                                    >
                                        <span className="inline-block text-right w-14">
                                            {option.label}
                                        </span>
                                        <span className="inline-block w-32 pl-4">
                                            {formatCurrency(option.value, { cents: 'always' })}
                                        </span>
                                    </p>
                                </button>
                            ))}
                            <button
                                type="button"
                                className="text-left p-6 pr-14 hover:bg-blue-10"
                                onClick={() => {
                                    onCustom();
                                    collapse();
                                }}
                                data-testid="tip-type-toggle"
                            >
                                <p className="font-medium text-gray-70">
                                    <span className="inline-block text-right w-14" />
                                    <span className="inline-block w-32 pl-4">Custom</span>
                                </p>
                            </button>
                        </DropdownPose>
                    )}
                </PoseGroup>
            </div>
        )}
    </Collapsible>
);

TipPresetSelect.propTypes = {
    onSelect: PropTypes.func.isRequired,
    onCustom: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
        })
    ).isRequired,
    selected: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
    }).isRequired,
};

export default TipPresetSelect;
